import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Trash2, RotateCcw, Search, Loader, AlertTriangle } from "lucide-react";
import PageHeader from "./PageHeader";
import { navigate, useSegmente } from "./router";

// Ein Ort fuer alles Geloeschte. Vorher war der Papierkorb nur ein Reiter
// innerhalb der Stellenanzeigen und kannte auch nur diese.

function formatDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
}

// Wie lange der Eintrag noch da ist. Das ist die Angabe, die zaehlt - ein
// Loeschdatum allein beantwortet die Frage nicht.
function verbleibend(geloeschtAm, tage) {
  if (!geloeschtAm) return null;
  const weg = new Date(geloeschtAm).getTime() + tage * 24 * 60 * 60 * 1000;
  const rest = Math.ceil((weg - Date.now()) / (24 * 60 * 60 * 1000));
  return rest;
}

export default function PapierkorbPage() {
  const [eintraege, setEintraege] = useState([]);
  const [katalog, setKatalog] = useState([]);
  const [tage, setTage] = useState(30);
  const [loading, setLoading] = useState(true);
  const [fehler, setFehler] = useState("");
  const [busy, setBusy] = useState("");
  const [suche, setSuche] = useState("");

  // Der Bereich steht in der Adresse: /papierkorb/karriereportal. So laesst
  // sich aus einem Bereich gezielt hierher verlinken, und Zurueck funktioniert.
  const segmente = useSegmente();
  const typFilter = segmente[0] === "papierkorb" ? segmente[1] || "" : "";
  const setTypFilter = (t) => navigate(t ? `/papierkorb/${t}` : "/papierkorb");

  const load = useCallback(() => {
    setLoading(true);
    fetch("/api/papierkorb/alles", { credentials: "include" })
      .then(async (r) => {
        const d = await r.json();
        if (!r.ok) throw new Error(d.error || "Laden fehlgeschlagen");
        return d;
      })
      .then((d) => {
        setEintraege(Array.isArray(d.eintraege) ? d.eintraege : []);
        setKatalog(Array.isArray(d.typen) ? d.typen : []);
        if (d.tage) setTage(d.tage);
        setFehler("");
      })
      .catch((e) => setFehler(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const schluessel = (e) => `${e.typ}:${e.id}`;

  const wiederherstellen = async (e) => {
    setBusy(schluessel(e));
    setFehler("");
    try {
      const res = await fetch(`/api/papierkorb/${e.typ}/${encodeURIComponent(e.id)}/wiederherstellen`, {
        method: "POST", credentials: "include",
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || "Wiederherstellen fehlgeschlagen");
      load();
    } catch (err) {
      setFehler(err.message);
    } finally {
      setBusy("");
    }
  };

  const endgueltig = async (e) => {
    const name = e.titel || e.zusatz || e.label;
    if (!window.confirm(`„${name}" endgültig löschen? Das lässt sich nicht rückgängig machen.`)) return;
    setBusy(schluessel(e));
    setFehler("");
    try {
      const res = await fetch(`/api/papierkorb/${e.typ}/${encodeURIComponent(e.id)}`, {
        method: "DELETE", credentials: "include",
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || "Löschen fehlgeschlagen");
      load();
    } catch (err) {
      setFehler(err.message);
    } finally {
      setBusy("");
    }
  };

  // Name des gewaehlten Bereichs - auch dann, wenn dort gerade nichts liegt.
  const gewaehlterName = useMemo(() => {
    const t = katalog.find((k) => k.typ === typFilter);
    return t ? t.label : typFilter;
  }, [katalog, typFilter]);

  // Je Bereich zaehlen, damit der Filter zeigt, was ueberhaupt drin liegt.
  const typen = useMemo(() => {
    const m = new Map();
    for (const e of eintraege) {
      if (!m.has(e.typ)) m.set(e.typ, { typ: e.typ, label: e.label, anzahl: 0 });
      m.get(e.typ).anzahl += 1;
    }
    return [...m.values()].sort((a, b) => a.label.localeCompare(b.label, "de"));
  }, [eintraege]);

  const gefiltert = useMemo(() => eintraege.filter((e) => {
    if (typFilter && e.typ !== typFilter) return false;
    if (!suche.trim()) return true;
    const s = suche.toLowerCase();
    return `${e.titel || ""} ${e.zusatz || ""} ${e.label || ""}`.toLowerCase().includes(s);
  }), [eintraege, suche, typFilter]);

  return (
    <>
      <PageHeader
        eyebrow="Verwaltung"
        title="Papierkorb"
        subtitle={`Alles Gelöschte aus allen Bereichen. Nach ${tage} Tagen wird es endgültig entfernt.`}
      />

      {fehler && <div className="card users-error">{fehler}</div>}

      {!loading && eintraege.length > 0 && (
        <div className="toolbar">
          <div className="search-input-wrap toolbar-search">
            <Search size={16} />
            <input
              type="text"
              className="search-input"
              placeholder="Im Papierkorb suchen …"
              value={suche}
              onChange={(e) => setSuche(e.target.value)}
            />
            {suche && <button className="search-clear" onClick={() => setSuche("")}>&times;</button>}
          </div>
          <button className={`btn-secondary ${typFilter === "" ? "active" : ""}`} onClick={() => setTypFilter("")}>
            Alle ({eintraege.length})
          </button>
          {typen.map((t) => (
            <button key={t.typ}
              className={`btn-secondary ${typFilter === t.typ ? "active" : ""}`}
              onClick={() => setTypFilter(typFilter === t.typ ? "" : t.typ)}>
              {t.label} ({t.anzahl})
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="loading-indicator"><div className="spinner" /><span>Lade Papierkorb …</span></div>
      ) : gefiltert.length === 0 ? (
        <div className="empty-card card">
          <Trash2 size={32} strokeWidth={1.5} />
          <p style={{ marginTop: 12 }}>
            {eintraege.length === 0
              ? "Der Papierkorb ist leer."
              : typFilter && !suche.trim()
                ? `Unter „${gewaehlterName}“ liegt gerade nichts.`
                : "Nichts gefunden."}
          </p>
          {typFilter && (
            <button className="btn-secondary" style={{ marginTop: 16 }} onClick={() => setTypFilter("")}>
              Alles anzeigen
            </button>
          )}
        </div>
      ) : (
        <div className="archive-list">
          {gefiltert.map((e) => {
            const rest = verbleibend(e.deleted_at, tage);
            const knapp = rest !== null && rest <= 3;
            return (
              <div className="archive-row card papierkorb-row" key={schluessel(e)}>
                <div className="archive-info">
                  <strong>{e.titel || <em>ohne Titel</em>}</strong>
                  <span className="archive-meta">
                    {e.label}
                    {e.zusatz && e.zusatz !== e.titel ? ` · ${e.zusatz}` : ""}
                    {` · gelöscht am ${formatDate(e.deleted_at)}`}
                  </span>
                </div>
                <div className="archive-actions">
                  <span className={`papierkorb-frist ${knapp ? "papierkorb-frist--knapp" : ""}`}>
                    {knapp && <AlertTriangle size={12} />}
                    {rest === null ? "" : rest <= 0 ? "läuft ab" : `noch ${rest} Tag${rest === 1 ? "" : "e"}`}
                  </span>
                  <button className="btn-secondary-sm" onClick={() => wiederherstellen(e)}
                    disabled={busy === schluessel(e)}>
                    {busy === schluessel(e)
                      ? <><Loader size={13} className="spin" /> …</>
                      : <><RotateCcw size={13} /> Wiederherstellen</>}
                  </button>
                  <button className="tk-card-icon-btn tk-card-icon-btn--danger" onClick={() => endgueltig(e)}
                    disabled={busy === schluessel(e)}
                    data-tooltip="Endgültig löschen" aria-label="Endgültig löschen">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
